import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Overline } from "../Reveal";

const Field = ({ label, testid, type = "text", value, onChange, textarea }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.18em] text-white/40">{label}</span>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={onChange} rows={5}
        className="w-full bg-transparent border-b border-white/15 py-3 mt-2 outline-none text-white placeholder:text-white/30 focus:border-[var(--gold)] transition-colors resize-none" />
    ) : (
      <input data-testid={testid} type={type} value={value} onChange={onChange}
        className="w-full bg-transparent border-b border-white/15 py-3 mt-2 outline-none text-white placeholder:text-white/30 focus:border-[var(--gold)] transition-colors" />
    )}
  </label>
);

export const ContactSection = ({ submitLabel = "Umów bezpłatną konsultację" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    if (!name || !email || !message) { setStatus({ ok: false, text: "Uzupełnij wymagane pola: imię, e-mail i wiadomość." }); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setStatus({ ok: false, text: "Podaj poprawny adres e-mail." }); return; }

    setSending(true);
    setStatus(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone: form.phone.trim(), company: form.company.trim(), message }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus({ ok: true, text: "Dziękujemy. Wiadomość została wysłana. Odezwiemy się wkrótce." });
    } catch (err) {
      setStatus({ ok: false, text: "Nie udało się wysłać wiadomości. Spróbuj ponownie za chwilę." });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-[#050505] text-white py-24 md:py-32 px-6" id="contact" data-testid="contact-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal><Overline dark>Kontakt</Overline></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-4xl sm:text-5xl lg:text-[3.25rem] tracking-tightest leading-[1.05] font-semibold">
              Porozmawiajmy o tym, czego potrzebuje <span className="font-serif-italic font-normal" style={{ color: "var(--gold)" }}>Twoja firma.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-white/55 text-lg max-w-md">
              Opowiedz nam, czego potrzebujesz. Podpowiemy, jakie rozwiązanie będzie najlepiej dopasowane do Twojej firmy i sposobu pracy.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-10 space-y-4 text-white/70">
              <a href="mailto:veliora.kontakt.pl@gmail.com" className="block hover:text-white transition-colors">veliora.kontakt.pl@gmail.com</a>
              <a href="tel:+48519615441" className="block hover:text-white transition-colors">+48 519 615 441</a>
              <p>Polska • współpraca online</p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={submit} noValidate className="space-y-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Imię i nazwisko" testid="contact-name" value={form.name} onChange={set("name")} />
              <Field label="E-mail" type="email" testid="contact-email" value={form.email} onChange={set("email")} />
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              <Field label="Telefon" type="tel" testid="contact-phone" value={form.phone} onChange={set("phone")} />
              <Field label="Firma" testid="contact-company" value={form.company} onChange={set("company")} />
            </div>
            <Field label="W czym możemy pomóc?" testid="contact-message" value={form.message} onChange={set("message")} textarea />
            <div className="pt-2">
              <motion.button whileHover={{ y: -2 }} type="submit" data-testid="contact-submit" disabled={sending}
                className="group inline-flex items-center gap-2 rounded-full bg-white text-black font-medium px-7 py-4 w-full sm:w-auto justify-center disabled:opacity-60">
                {sending ? "Wysyłanie…" : submitLabel} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </motion.button>

              {status && (
                <p
                  data-testid={status.ok ? "contact-success" : "contact-error"}
                  className="mt-6 mb-4 text-[15px] leading-relaxed max-w-md"
                  style={{ color: status.ok ? "#F5F3EE" : "#E8938B" }}
                >
                  {status.text}
                </p>
              )}

              <p className="mt-4 text-sm text-white/40 leading-relaxed max-w-md">
                Bez zobowiązań. Najpierw poznajemy Twoje potrzeby i sprawdzamy, czy możemy Ci pomóc.
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
